import { ReferenceDataEditionError } from '../core/domain/errors/ReferenceDataEditionError';
import { ReferenceDataValidationError } from '../core/domain/errors/ReferenceDataValidationError';
import { ReferenceGeneration, RoadSection, RoadSectionId } from '../core/domain/Reference';
import { ReferenceGenerationError } from '../core/domain/errors/ReferenceGenerationError';
import { ExternalReferencePort } from '../core/reference/ExternalReferencePort';
import { captureException } from '@sentry/browser';
import axios from 'axios';

export class ExternalReferenceProvider implements ExternalReferencePort {
    constructor(private readonly baseEndpoint: string) {}

    async loadRoadSections(): Promise<RoadSection[]> {
        try {
            const response = await axios.request({
                method: 'GET',
                url: `${this.baseEndpoint}/reference`,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async editRoadSections(roadSectionIds: RoadSectionId[], data: Partial<RoadSection>): Promise<RoadSection[]> {
        try {
            const response = await axios.request({
                method: 'PATCH',
                url: `${this.baseEndpoint}/reference`,
                data: {
                    roadSectionIds,
                    data,
                },
                withCredentials: true,
            });

            return response.data;
        } catch (err: any) {
            console.error(err);
            captureException(err);
            if (err.response?.status === 422) {
                const error = err.response.data.details[0];
                const field = error.field || 'unknown';
                throw new ReferenceDataValidationError(field);
            } else {
                throw new ReferenceDataEditionError('Error while editing road sections');
            }
        }
    }

    async startReferenceGeneration(): Promise<ReferenceGeneration> {
        try {
            const response = await axios.request({
                method: 'POST',
                url: `${this.baseEndpoint}/reference/generation-jobs`,
                withCredentials: true,
            });

            return response.data;
        } catch (err: any) {
            const status = err.response?.status;
            if (status === 409) {
                throw new ReferenceGenerationError('already_running');
            }
            console.error(err);
            captureException(err);
            if (status === 422) {
                throw new ReferenceGenerationError('no_sessions');
            }
            if (status === 503) {
                throw new ReferenceGenerationError('unavailable');
            }
            throw new ReferenceGenerationError('unknown');
        }
    }

    async loadReferenceGenerations(): Promise<ReferenceGeneration[]> {
        try {
            const response = await axios.request({
                method: 'GET',
                url: `${this.baseEndpoint}/reference/generation-jobs`,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }
}
